import React from "react";
import {
  Box,
  Checkbox,
  Chip,
  Typography,
  TablePagination,
  Avatar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { format } from "date-fns";

const TicketTable = ({
  tickets,
  selected,
  page,
  rowsPerPage,
  totalCount,
  onSelectAllClick,
  onSelect,
  handleChangePage,
  handleChangeRowsPerPage,
  onRowClick,
}) => {
  // Status color mapping
  const getStatusColor = (status) => {
    switch (status) {
      case "Open":
        return "info";
      case "Pending":
        return "warning";
      case "In Progress":
        return "primary";
      case "Resolved":
        return "success";
      case "Closed":
        return "default";
      default:
        return "default";
    }
  };

  // Priority color mapping
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Low":
        return "default";
      case "Medium":
        return "info";
      case "High":
        return "warning";
      case "Urgent":
        return "error";
      default:
        return "default";
    }
  };

  // Helper to safely format dates
  const safeFormatDate = (dateValue) => {
    if (!dateValue) return "-";
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "-";
      return format(date, "MMM dd, yyyy HH:mm");
    } catch (error) {
      console.error("Error formatting date:", dateValue, error);
      return "-";
    }
  };

  // Prepare data for DataGrid
  const rows = tickets.map((ticket, index) => ({
    ...ticket,
    serialNumber: page * rowsPerPage + index + 1,
    messageCount: ticket.messages?.length || 0,
    formattedCreatedAt: safeFormatDate(ticket.createdAt),
    formattedUpdatedAt: safeFormatDate(ticket.updatedAt),
  }));

  // Define columns for DataGrid
  const columns = [
    {
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      renderHeader: () => (
        <Checkbox
          indeterminate={selected.length > 0 && selected.length < totalCount}
          checked={totalCount > 0 && selected.length === totalCount}
          onChange={onSelectAllClick}
          inputProps={{ "aria-label": "select all tickets" }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selected.includes(params.row.id)}
          onClick={(event) => {
            event.stopPropagation();
            onSelect(event, params.row.id);
          }}
          inputProps={{ "aria-labelledby": `select-${params.row.id}` }}
        />
      ),
    },
    {
      field: "ticketNumber",
      headerName: "Ticket #",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600} color="primary">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "subject",
      headerName: "Subject",
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500} noWrap>
            {params.value}
          </Typography>
          {params.row.customerName && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.customerName}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getPriorityColor(params.value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "assignedTo",
      headerName: "Assigned To",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => {
        if (!params.value) {
          return (
            <Typography variant="body2" color="text.secondary">
              Unassigned
            </Typography>
          );
        }
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              src={params.value.avatar}
              alt={params.value.name}
              sx={{ width: 24, height: 24 }}
            >
              {params.value.name?.charAt(0)}
            </Avatar>
            <Typography variant="body2" noWrap>
              {params.value.name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "formattedUpdatedAt",
      headerName: "Updated",
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {params.value}
        </Typography>
      ),
    },
    {
      field: "messageCount",
      headerName: "Messages",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{ minWidth: 40 }}
        />
      ),
    },
  ];

  return (
    <>
      <Box sx={{ height: "auto", width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          hideFooter={true}
          autoHeight
          disableColumnMenu
          disableSelectionOnClick
          checkboxSelection={false}
          rowHeight={60}
          headerHeight={56}
          getRowId={(row) => row.id}
          onRowClick={(params) => onRowClick(params.row)}
          sx={{
            boxShadow: 0,
            border: 0,
            "& .MuiDataGrid-cell": {
              padding: "8px 16px",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus": {
              outline: "none",
            },
            "& .MuiDataGrid-row": {
              cursor: "pointer",
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.palette.mode === "light"
                    ? "rgba(0, 0, 0, 0.04)"
                    : "rgba(255, 255, 255, 0.08)",
              },
            },
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          padding: "8px 16px",
          borderTop: "1px solid rgba(224, 224, 224, 1)",
        }}
      >
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-toolbar": {
              alignItems: "center",
              minHeight: "40px",
              padding: "0 8px",
            },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                margin: 0,
                display: "flex",
                alignItems: "center",
              },
            ".MuiTablePagination-select": {
              marginRight: "8px",
              marginLeft: "8px",
            },
            ".MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </Box>
    </>
  );
};

export default TicketTable;
